import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Banner from "../../../DesignSystem/Banner/Banner";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { canOverrideClosedChargeMonths } from "../utils/closedChargeMonths";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import AddCharge from "./AddCharge";
import "../../../../css/Finance/MonthlyPayments.css";

const keyOf = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const AdviserChargePage = () => {
  const navigate = useNavigate();
  const { userPermissions = [] } = useUserPermissions();
  const { adviserEmail: routeAdviser } = useParams();
  const [searchParams] = useSearchParams();
  const {
    selectedMonth,
    setSelectedMonth,
    monthOptions,
    chargeRecords,
    advisersByEmail,
    selectedMonthClosed,
  } = useAdviserCharges();
  const { advisers } = useAdviserList();
  const email = keyOf(routeAdviser);
  const requestedMonth = searchParams.get("month");
  const canOverrideClosedCharges = canOverrideClosedChargeMonths(userPermissions);
  const chargeId = searchParams.get("charge");

  useEffect(() => {
    if (
      monthOptions.some((option) => option.value === requestedMonth) &&
      selectedMonth !== requestedMonth
    ) {
      setSelectedMonth(requestedMonth);
    }
  }, [monthOptions, requestedMonth, selectedMonth, setSelectedMonth]);

  const adviser = useMemo(() => {
    const fromList = advisers.find(
      (item) => keyOf(item.email || item.id) === email,
    );
    const fromCharges = advisersByEmail[email]?.adviser;
    const charge = chargeRecords.find(
      (row) => keyOf(row.adviserEmail) === email,
    );
    return {
      name:
        fromList?.name ||
        fromCharges?.adviser ||
        charge?.adviser ||
        routeAdviser,
      brand:
        fromList?.brandName ||
        fromCharges?.brandName ||
        fromCharges?.practice ||
        "-",
      email:
        fromList?.email || fromCharges?.adviserEmail || email || routeAdviser,
      id: fromList?.adviserId || fromCharges?.adviserId || "",
    };
  }, [advisers, advisersByEmail, chargeRecords, email, routeAdviser]);

  const charge = useMemo(
    () => chargeRecords.find((row) => row.id === chargeId),
    [chargeId, chargeRecords],
  );
  const breakdownUrl = `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(adviser.email)}?month=${selectedMonth}`;
  const title =
    charge?.manualChargeType || charge?.chargeType || "Add New Charge";

  return (
    <div className="Main-Page">
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container monthly-payments-page table-contained-page column gap-m">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            {
              label: "Monthly Payments",
              to: APP_ROUTES.CHARGES_SUMMARY_DASHBAORD,
            },
            {
              label: "Charges Breakdown",
              to: `${APP_ROUTES.CHARGES_BREAKDOWN}?month=${selectedMonth}`,
            },
            { label: adviser.name, to: breakdownUrl },
            { label: title },
          ]}
        />
        <h3 className="FP_H3">{title}</h3>
        {selectedMonthClosed ? (
          <Banner
            type="info"
            message={
              canOverrideClosedCharges
                ? "This month is closed. Finance override is enabled for charges."
                : "This month is closed. Its charges can no longer be added, edited, or removed."
            }
          />
        ) : null}
        {!selectedMonthClosed || canOverrideClosedCharges ? (
          <AddCharge
            asPage
            adviser={adviser}
            charge={charge}
            userPermissions={userPermissions}
            defaultDate={`${requestedMonth || selectedMonth}-01`}
            onClose={() => navigate(breakdownUrl)}
          />
        ) : null}
      </main>
    </div>
  );
};

export default withMonthlyPaymentsAccess(AdviserChargePage);
